import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type LocationStatus = 'active' | 'archived';

export type LocationSummary = {
  id: string;
  code: string;
  name: string;
  addressLine: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  countryCode: string | null;
  timezone: string | null;
  status: LocationStatus;
  createdAt: string;
  updatedAt: string;
};

export type LocationListResponse = {
  items: LocationSummary[];
  total: number;
  page: number;
  pageSize: number;
};

const LIST_KEY = ['locations'] as const;
const ONE_KEY = (id: string) => ['locations', id] as const;

export function useLocations(filter: { status?: LocationStatus[] } = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, filter] as const,
    queryFn: async (): Promise<LocationListResponse> => {
      const params: Record<string, string | number> = { pageSize: 200 };
      if (filter.status && filter.status.length > 0) {
        params['status'] = filter.status.join(',');
      }
      const res = await apiClient.get<LocationListResponse>('/locations', { params });
      return res.data;
    },
  });
}

export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['locations', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<LocationSummary> => {
      const res = await apiClient.get<LocationSummary>(`/locations/${id}`);
      return res.data;
    },
  });
}

export type LocationCreateBody = {
  code: string;
  name: string;
  addressLine?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
  timezone?: string | null;
};

export function useCreateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LocationCreateBody) => {
      const res = await apiClient.post<LocationSummary>('/locations', body);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export type LocationUpdateBody = Partial<Omit<LocationCreateBody, 'code'>>;

export function useUpdateLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: LocationUpdateBody }) => {
      const res = await apiClient.patch<LocationSummary>(`/locations/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useArchiveLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<LocationSummary>(`/locations/${id}/archive`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export function useUnarchiveLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<LocationSummary>(`/locations/${id}/unarchive`, {});
      return res.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(id) });
    },
  });
}

export const STATUS_LABEL: Record<LocationStatus, string> = {
  active: 'Active',
  archived: 'Archived',
};

/**
 * Catalogue keys for the map above. The map stays as the baked English, so a
 * caller resolves a label as `t(STATUS_LABEL_KEY[s], STATUS_LABEL[s])`. Each
 * key is written out rather than composed from the status at the call site:
 * the parity guard reads source for literal keys.
 */
export const STATUS_LABEL_KEY: Record<LocationStatus, string> = {
  active: 'locations.vocab.statusActive',
  archived: 'locations.vocab.statusArchived',
};
